import React, { useEffect, useLayoutEffect, useState } from "react";
import { Button, Input, message, Form, Spin, Row, Col } from "antd";
import {
  MailOutlined,
  LockOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import Service from "../service";
import {
  showAuthLoader,
  hideAuthLoader,
  userSignInSuccess,
} from "../appRedux/actions/Auth";
import IntlMessages from "../util/IntlMessages";
import moment from "moment";
import { setIsPasswordChange } from "../appRedux/actions/Common";
import { setSharedSso, getSharedSso, isLogoutPending } from "../utils/ssoCookie";
import { GLOBAL_CONFIG, ORGANIZATION_TREE } from "../constants/permission_id";
import { Modal, Typography } from "antd";
import { Helmet } from "react-helmet";
import AuthBrandingSection from "../components/Login/AuthBrandingSection";
import SecurityBadge from "../components/Login/SecurityBadge";
import TwoFactorOtpInput from "../components/Login/TwoFactorOtpInput";
import { signInWithMicrosoft } from "../utils/azureAuth";
import { saveAtsApproverFlag } from "../util/atsApproverAccess";

// The four-square Microsoft logo (brand colours), drawn in CSS - no asset.
const MICROSOFT_LOGO_COLORS = ["#f25022", "#7fba00", "#00a4ef", "#ffb900"];

const API_URL =
  `${process.env.REACT_APP_API_URL}/uploads/company_image`;

const SignIn = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirect_uri = queryParams.get("redirect_uri");
  const tokenFromQuery = queryParams.get("token");
  const companySlugLocal = localStorage.getItem("companyDomain");
  const dispatch = useDispatch();
  const history = useHistory();
  const { alertMessage, showMessage, loader } = useSelector(({ auth }) => auth);
  const { companyslug, token } = useParams();
  const { Title, Text } = Typography;
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [isLogoBroken, setIsLogoBroken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [companyName, setCompanyName] = useState("");
  // Two-Factor Authentication (email OTP) step state
  const [mfa, setMfa] = useState({ required: false, mfaToken: null, email: null });
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Microsoft (Azure AD) sign-in - shown only when the server/company has it on
  const [azureConfig, setAzureConfig] = useState({ enabled: false });
  const [azureSigningIn, setAzureSigningIn] = useState(false);

  // Never keep credentials/remember-me values (legacy global keys were shared by every tenant on one origin).
  useEffect(() => {
    try {
      ["rememberMe", "remember_me", "rememberedEmail", "remembered_email", "savedEmail", "saved_email", "lastLoginEmail", "login_email", "userEmail", "email", "password"].forEach((k) => localStorage.removeItem(k));
    } catch (e) { /* storage unavailable */ }
  }, []);

  useEffect(() => {
    const fetchAzureConfig = async () => {
      try {
        const response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.getAzureSsoConfig,
          params: companyslug ? { slug: companyslug } : undefined,
        });
        setAzureConfig(response?.data?.data?.enabled ? response.data.data : { enabled: false });
      } catch (error) {
        setAzureConfig({ enabled: false });
      }
    };
    fetchAzureConfig();
  }, [companyslug]);

  // Count down the resend cooldown so the button shows "Resend in Ns".
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (tokenFromQuery) {
      loginWithTokenFlow(tokenFromQuery);
    } else if (token) {
      dataForVerfy();
    } else {
      // Auto-login from the shared cross-product cookie: a sibling app
      // (Registration / CRM / Payroll) is logged in this browser, so log in
      // here too without the user typing anything.
      const sharedToken = getSharedSso();
      const localToken = localStorage.getItem("accessToken");
      if (!localToken && sharedToken && !isLogoutPending()) {
        loginWithTokenFlow(sharedToken);
      }
    }
  }, [token, tokenFromQuery]);

  const dispatchLocalStorageChange = (key, newValue) => {
    window.dispatchEvent(
      new CustomEvent("localStorageChange", {
        detail: { key, newValue },
      })
    );
  };

  const fetchCompanyAssets = async (companyslug) => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.getCompanyMedia,
        body: { slug: companyslug },
      });

      if (response?.data) {
        const { companyLogoUrl, companyFavIcoUrl, companyName } = response.data.data;
        setCompanyName(companyName || "");
        if (companyLogoUrl) {
          setLogoUrl(companyLogoUrl);
          localStorage.setItem(`LogoUrl_${companyslug}`, companyLogoUrl);
          dispatchLocalStorageChange(`LogoUrl_${companyslug}`, companyLogoUrl);
        }
        if (companyFavIcoUrl) {
          setFaviconUrl(companyFavIcoUrl);
          localStorage.setItem(`FavIcon_${companyslug}`, companyFavIcoUrl);
          dispatchLocalStorageChange(
            `FavIcon_${companyslug}`,
            companyFavIcoUrl
          );
        }
      }
    } catch (error) {
      console.error("Error fetching company assets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (companyslug) {
      const cachedLogoUrl = localStorage.getItem(`LogoUrl_${companyslug}`);
      const cachedFaviconUrl = localStorage.getItem(`FavIcon_${companyslug}`);

      if (cachedLogoUrl) {
        setLogoUrl(cachedLogoUrl);
        setIsLogoBroken(false);
      }
      if (cachedFaviconUrl) {
        setFaviconUrl(cachedFaviconUrl);
      }

      setIsLoading(!cachedLogoUrl || !cachedFaviconUrl);
      fetchCompanyAssets(companyslug);
    } else {
      setIsLoading(false);
    }
  }, [companyslug]);

  useEffect(() => {
    // reset broken flag when logo url changes
    setIsLogoBroken(false);
  }, [logoUrl]);

  useEffect(() => {
    if (companyslug) {
      const handleStorageChange = (e) => {
        if (e.key === `FavIcon_${companyslug}` || e.key === null) {
          const cachedFaviconUrl = localStorage.getItem(`FavIcon_${companyslug}`);
          if (cachedFaviconUrl) {
            setFaviconUrl(cachedFaviconUrl);
          }
        }
      };

      const handleCustomStorageChange = (e) => {
        if (e.detail.key === `FavIcon_${companyslug}`) {
          setFaviconUrl(e.detail.newValue);
        }
      };

      window.addEventListener("storage", handleStorageChange);
      window.addEventListener("localStorageChange", handleCustomStorageChange);

      return () => {
        window.removeEventListener("storage", handleStorageChange);
        window.removeEventListener("localStorageChange", handleCustomStorageChange);
      };
    }
  }, [companyslug]);

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const SSORedirect = async (response) => {
    if (response?.data && response?.data?.data) {
      window.open(
        `${redirect_uri}?token=${response.data.tokenForSSO.accessToken}`,
        "_self"
      );
    }
  };

  const handleLoginSuccess = (response, slugOverride) => {
    console.log("response654", response);
    const userData = response.data.data;
    const resolvedSlug =
      slugOverride || companyslug || userData?.companyId?.companyDomain;

    if (!resolvedSlug) {
      message.error("Company slug missing in response.");
      return;
    }

    if (redirect_uri) {
      SSORedirect(response);
      return;
    }

    dispatch(setIsPasswordChange(userData.isPasswordChanged));

    localStorage.setItem("user_data", JSON.stringify(userData));
    localStorage.setItem("accessToken", response.data.authToken.accessToken);
    localStorage.setItem("ssoToken", response.data.tokenForSSO.accessToken);
    // Mirror the slim SSO token into the shared `.weekmate.in` cookie so sibling
    // apps auto-login in the same browser. Covers both password login and the
    // token-login flow (loginWithTokenFlow → handleLoginSuccess).
    setSharedSso(response.data.tokenForSSO.accessToken);
    localStorage.setItem("refreshToken", response.data.authToken.refreshToken);
    localStorage.setItem("companyDomain", resolvedSlug);
    localStorage.setItem(
      "Last Login Time",
      moment().format("DD-MM-YYYY HH:mm:ss")
    );
    localStorage.setItem(
      "resourceAccess",
      JSON.stringify(response.data.permissions)
    );
    localStorage.setItem(
      "sidebarModuleAccess",
      JSON.stringify(
        Array.isArray(response?.data?.sidebarModules)
          ? response.data.sidebarModules
          : []
      )
    );
    saveAtsApproverFlag(response?.data?.atsApprover);
    if (response.data.permissionsUpdatedAt) {
      localStorage.setItem("permissionsUpdatedAt", String(response.data.permissionsUpdatedAt));
    } else {
      localStorage.removeItem("permissionsUpdatedAt");
    }
    localStorage.setItem(
      "enableAtsFullFlow",
      String(Boolean(response?.data?.data?.companyId?.enableAtsFullFlow))
    );
    localStorage.setItem(
      `FavIcon_${resolvedSlug}`,
      response?.data?.data?.companyId.companyFavIcoUrl
    );
    localStorage.setItem(
      `LogoUrl_${resolvedSlug}`,
      response?.data?.data?.companyId.companyLogoUrl
    );

    const faviconKey = `FavIcon_${resolvedSlug}`;
    const logoKey = `LogoUrl_${resolvedSlug}`;

    dispatchLocalStorageChange(
      faviconKey,
      response?.data?.data?.companyId.companyFavIcoUrl
    );
    dispatchLocalStorageChange(
      logoKey,
      response?.data?.data?.companyId.companyLogoUrl
    );
    dispatch(userSignInSuccess(userData));
    history.push(`/${resolvedSlug}/dashboard`);
  };

  const loginWithTokenFlow = async (tokenValue) => {
    try {
      dispatch(showAuthLoader());
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.loginWithToken,
        body: { token: tokenValue },
      });
      dispatch(hideAuthLoader());

      if (response?.data && response?.data?.data) {
        handleLoginSuccess(
          response,
          response?.data?.data?.companyId?.companyDomain
        );
      } else {
        message.error(response?.data?.message || "Unable to login with token");
      }
    } catch (error) {
      dispatch(hideAuthLoader());
      message.error(
        error?.response?.data?.message || "Unable to login with token"
      );
    }
  };

  const onFinish = async (values) => {
    try {
      const reqBody = {
        email: values.email,
        password: values.password,
        slug: companyslug,
      };
      dispatch(showAuthLoader());
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.login,
        body: reqBody,
      });
      dispatch(hideAuthLoader());

      if (response?.data?.mfaRequired) {
        // Company has 2FA enabled - switch to the OTP verification step.
        setMfa({
          required: true,
          mfaToken: response.data.mfaToken,
          email: response.data.email,
        });
        setOtp("");
      } else if (response?.data && response?.data?.data) {
        handleLoginSuccess(response);
      } else {
        const msg = response.data.message;
        const index = Service.add_message(msg);
        if (index === -1) {
          message.error(msg).then(() => {
            Service.remove_message(msg);
          });
        }
      }
    } catch (error) {
      dispatch(hideAuthLoader());
      console.log(error);
    }
  };

  const handleMicrosoftSignIn = async () => {
    if (azureSigningIn) return;
    setAzureSigningIn(true);
    try {
      const idToken = await signInWithMicrosoft({
        clientId: azureConfig.clientId,
        authority: azureConfig.authority,
      });
      if (!idToken) return; // popup closed by the user

      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.azureSSOLogin,
        body: { idToken, slug: companyslug },
      });
      if (response?.data && response?.data?.data) {
        handleLoginSuccess(response);
      } else {
        message.error(response?.data?.message || "Microsoft sign-in failed");
      }
    } catch (error) {
      console.error("Microsoft sign-in error:", error);
      message.error(
        error?.errorCode === "popup_window_error"
          ? "Please allow pop-ups for this site to sign in with Microsoft."
          : "Microsoft sign-in failed. Please try again."
      );
    } finally {
      setAzureSigningIn(false);
    }
  };

  const handleVerifyOtp = async (code) => {
    const value = String(code ?? otp ?? "").trim();
    if (value.length < 6) {
      message.error("Please enter the 6-digit verification code.");
      return;
    }
    setVerifying(true);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.verifyMfa,
        body: { mfaToken: mfa.mfaToken, otp: value },
      });
      if (response?.data && response?.data?.data) {
        handleLoginSuccess(response);
      } else {
        message.error(response?.data?.message || "Verification failed");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resending || resendCooldown > 0) return;
    setResending(true);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.resendMfa,
        body: { mfaToken: mfa.mfaToken },
      });
      if (response?.data?.status === 200) {
        message.success(response.data.message || "A new code has been sent.");
        setOtp("");
        setResendCooldown(30);
      } else {
        message.error(response?.data?.message || "Could not resend the code");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Could not resend the code");
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setMfa({ required: false, mfaToken: null, email: null });
    setOtp("");
  };

  const dataForVerfy = async () => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.verifyRegistration,
        body: { token: token },
      });

      if (response?.data && response?.status === 200) {
        showVerificationModal(response?.message, true);
      } else {
        history.push("/");
      }
    } catch (error) {
      message.error(error?.response?.data?.message);
      history.push("/");
    }
  };

  const showVerificationModal = (message, verfyed) => {
    const modal = Modal.success({
      title: (
        <Title level={4} style={{ marginBottom: 0 }}>
          {verfyed ? "Verified" : "Not Verified"}
        </Title>
      ),
      content: (
        <div style={{ marginTop: 8 }}>
          <Text>
            Your email has {verfyed ? "" : "not"} been successfully verified.
          </Text>
          <br />
          <Text type="secondary">{message}</Text>
          <br />
          <br />
          <Button
            type="primary"
            block
            size="large"
            onClick={() => {
              modal.destroy();
            }}
            className="ant-btn-primary"
          >
            Ok
          </Button>
        </div>
      ),
      okText: null,
      footer: null,
      centered: true,
    });
  };

  return (
    <>
      <Helmet>
        <link
          rel="icon"
          type="image/png"
          href={
            faviconUrl
              ? `${API_URL}/${faviconUrl}`
              : "/HrmsLogo2.svg"
          }
        />
      </Helmet>
      <div className="main-login-page-wrapper">
        <Row className="login-container-row">
          {/* Left Side - Branding Section */}
          <Col xs={24} lg={14} className="login-branding-section">
            <AuthBrandingSection />
          </Col>

          {/* Right Side - Form Section */}
          <Col xs={24} lg={10} className="login-form-section">
            <div className="form-content">
              <div className="form-card">
                {/* Company logo (multi-tenant) shown above the form when available */}
                {isLoading ? (
                  <div className="company-logo company-logo--loading">
                    <Spin size="large" />
                  </div>
                ) : logoUrl && !isLogoBroken ? (
                  <div className="company-logo">
                    <img
                      alt={companyName || "Company"}
                      src={`${API_URL}/${logoUrl}`}
                      onError={() => setIsLogoBroken(true)}
                    />
                  </div>
                ) : null}

                {mfa.required ? (
                  <div className="login-form-wrapper mfa-step">
                    <div className="form-header">
                      <h2>Verify it's you 🔐</h2>
                      <p>
                        We've emailed a verification code to{" "}
                        <strong>{mfa.email}</strong>. Enter it below to continue.
                      </p>
                    </div>

                    <div className="form-fields">
                      <div className="field-group">
                        <label>Verification code</label>
                        <TwoFactorOtpInput
                          value={otp}
                          disabled={verifying}
                          onChange={setOtp}
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <Button
                        type="primary"
                        className="btn-signin"
                        loading={verifying}
                        disabled={verifying || otp.length < 6}
                        onClick={() => handleVerifyOtp()}
                        block
                      >
                        Verify & Sign In
                      </Button>
                    </div>

                    <div className="mfa-actions-row">
                      <Button type="link" className="mfa-link" onClick={handleBackToLogin}>
                        <ArrowLeftOutlined /> Back to login
                      </Button>
                      <Button
                        type="link"
                        className="mfa-link"
                        onClick={handleResendOtp}
                        loading={resending}
                        disabled={resending || resendCooldown > 0}
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                      </Button>
                    </div>
                  </div>
                ) : (
                <Form
                  name="basic"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  layout="vertical"
                  className="login-form-wrapper"
                >
                  <div className="form-header">
                    <h2>Welcome back! 👋</h2>
                    <p>
                      {`Sign in to continue to ${
                        companyName ? `${companyName} ` : ""
                      }HRMS`}
                    </p>
                  </div>

                  <div className="form-fields">
                    <div className="field-group">
                      <label>Email address</label>
                      <Form.Item
                        name="email"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your email!",
                          },
                          {
                            type: "email",
                            message: "Please enter valid email!",
                          },
                        ]}
                      >
                        <Input
                          type="email"
                          size="large"
                          name="login-email"
                          autoComplete="off"
                          placeholder="Enter your email"
                          prefix={<MailOutlined className="input-icon" />}
                        />
                      </Form.Item>
                    </div>

                    <div className="field-group">
                      <label>Password</label>
                      <Form.Item
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your password!",
                          },
                        ]}
                      >
                        <Input.Password
                          size="large"
                          autoComplete="new-password"
                          placeholder="Enter your password"
                          prefix={<LockOutlined className="input-icon" />}
                        />
                      </Form.Item>
                    </div>

                    <div className="form-options">
                      <Link className="forgot-password-link" to="/forgot-password">
                        Forgot password?
                      </Link>
                    </div>
                  </div>

                  <div className="form-actions">
                    <Form.Item noStyle>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="btn-signin"
                        loading={loader}
                      >
                        <IntlMessages id="app.userAuth.signIn" />{" "}
                        <ArrowRightOutlined />
                      </Button>
                    </Form.Item>
                  </div>

                  {azureConfig.enabled && (
                    <>
                      <div className="sso-divider">or</div>
                      <Button
                        className="btn-microsoft"
                        onClick={handleMicrosoftSignIn}
                        loading={azureSigningIn}
                      >
                        {!azureSigningIn && (
                          <span className="microsoft-logo" aria-hidden="true">
                            {MICROSOFT_LOGO_COLORS.map((color) => (
                              <span key={color} style={{ background: color }} />
                            ))}
                          </span>
                        )}
                        Sign in with Microsoft
                      </Button>
                      {azureConfig.microsoftOnly && (
                        <p className="sso-only-note">
                          Your organisation uses Microsoft sign-in. Password login is for admins only.
                        </p>
                      )}
                    </>
                  )}
                </Form>
                )}
              </div>

              <SecurityBadge />
            </div>
          </Col>
        </Row>

        {showMessage ? message.error(alertMessage.toString()) : null}
      </div>
    </>
  );
};

export default SignIn;